'use client'

import { useCallback, useEffect, useRef, useState } from 'react'

import { useLazyGetConversationQuery } from '~/data/api/client'
import { logger } from '~/debug/logger'
import type { MessageAttachment } from '~/features/assistant/attachments'
import mapPersistedMessages from '~/features/assistant/conversationHistory'
import type { AssistantClarificationQuestion, AssistantStreamEvent, AssistantToolCall } from '~/features/assistant/streamTypes'
import { billingDraftsFromTrace, parseToolTrace } from '~/features/assistant/toolTrace'
import useAssistantAttachments from '~/features/assistant/useAssistantAttachments'
import useAssistantStream from '~/features/assistant/useAssistantStream'
import type { ActionProposal } from '~/features/assistant-v2/stream/dataParts'

import { BILLING_COMPOSER_SKILL_ID, buildComposerContextRefs } from './contextRefs'
import { readSessionConversationId, writeSessionConversationId } from './pendingDraft'
import type {
  BillingCompleteness,
  BillingComposerEntity,
  BillingComposerMode,
  BillingDraft,
  BillingDraftFields,
  BillingTaxGuidance
} from './types'
import useSessionScope from './useSessionScope'

export type ComposerClarification = { intro?: string; questions: AssistantClarificationQuestion[] }

export type ComposerMessage = {
  id: string
  role: 'user' | 'assistant'
  text: string
  thinking?: string
  toolCalls?: AssistantToolCall[]
  /** Files sent with a user turn, shown as chips under the bubble. */
  attachments?: MessageAttachment[]
  streaming?: boolean
  /** Drafts proposed on this assistant turn, rendered as cards under the bubble. */
  draftIds?: string[]
  clarification?: ComposerClarification
  clarificationAnswers?: Record<string, string>
  clarificationRetired?: boolean
  /** Mutations proposed on this assistant turn, each awaiting the operator's approval. */
  proposals?: ActionProposal[]
}

export type BillingComposerState = {
  messages: ComposerMessage[]
  /** Every draft received this conversation, oldest first. */
  drafts: BillingDraft[]
  /** Most recent tax guidance from any draft. */
  taxGuidance: BillingTaxGuidance | null
  /** Most recent backend-computed completeness from any draft. */
  completeness: BillingCompleteness | null
  awaitingAnswers: boolean
  busy: boolean
  status: string | null
  error: string | null
  /** True while a previous conversation is being restored from the session. */
  resuming: boolean
}

const INITIAL_STATE: BillingComposerState = {
  messages: [],
  drafts: [],
  taxGuidance: null,
  completeness: null,
  awaitingAnswers: false,
  busy: false,
  status: null,
  error: null,
  resuming: false
}

export type UseBillingComposerInput = {
  organizationId: string | undefined
  entity?: BillingComposerEntity
  mode: BillingComposerMode
  entityId?: string
  entityName?: string
  pathname: string
  pageTitle: string
  /** Returns the live form converted to the draft-fields shape; undefined when the page has no form. */
  getFormSnapshot?: () => BillingDraftFields | undefined
}

type SendOptions = {
  /** Attach the current form as a `billing_draft` context ref (default true when a form exists). */
  includeForm?: boolean
  /** Friendly text for the user bubble when it differs from the message sent to the model. */
  displayText?: string
}

/**
 * Drives one billing-composer conversation: sends turns with the billing skill and the page/form context,
 * reduces the stream into a transcript plus structured drafts / tax guidance / completeness, and keeps the
 * conversation id in the session so closing and reopening the panel on the same page resumes the thread.
 */
export function useBillingComposer(input: UseBillingComposerInput) {
  const { organizationId, entity, mode, entityId, entityName, pathname, pageTitle, getFormSnapshot } = input
  const sessionScope = useSessionScope()
  const {
    sendMessage,
    ensureConversation,
    abort: abortStream,
    resetConversation,
    resumeConversation,
    getActiveConversationId
  } = useAssistantStream(organizationId)
  const [fetchConversation] = useLazyGetConversationQuery()
  const [state, setState] = useState<BillingComposerState>(INITIAL_STATE)
  const resumeAttemptedRef = useRef(false)

  // Remember the conversation id after each turn so a reopen (or reload) on this page can resume it.
  const persistConversationId = useCallback(() => {
    writeSessionConversationId(pathname, getActiveConversationId(), sessionScope)
  }, [getActiveConversationId, pathname, sessionScope])

  // Uploads create the conversation when needed, so the id is persisted from there too.
  const attachments = useAssistantAttachments({
    organizationId,
    ensureConversation,
    onConversationReady: persistConversationId
  })
  const { waitForUploads, takeReady, clear: clearAttachments } = attachments

  const send = useCallback(
    async (text: string, options?: SendOptions) => {
      const trimmed = text.trim()
      if (!trimmed || !organizationId) return

      // Uploads that are still in flight finish first so their ids ride along on this turn.
      await waitForUploads()
      const sentAttachments = takeReady()

      const userLineId = crypto.randomUUID()
      const assistantLineId = crypto.randomUUID()

      setState((prev) => ({
        ...prev,
        busy: true,
        error: null,
        status: null,
        awaitingAnswers: false,
        messages: [
          ...prev.messages.map((line) =>
            line.clarification && !line.clarificationAnswers ? { ...line, clarificationRetired: true } : line
          ),
          {
            id: userLineId,
            role: 'user',
            text: options?.displayText?.trim() || trimmed,
            attachments: sentAttachments.length > 0 ? sentAttachments : undefined
          },
          { id: assistantLineId, role: 'assistant', text: '', streaming: true }
        ]
      }))

      const includeForm = options?.includeForm ?? true
      const formSnapshot = includeForm ? getFormSnapshot?.() : undefined
      const contextRefs = buildComposerContextRefs({
        entity,
        mode,
        entityId,
        entityName,
        formSnapshot,
        pathname,
        pageTitle
      })

      // The stream hook creates the conversation before the first frame, so the id is known by the
      // first event; persist it then rather than after the turn so closing the panel mid-stream keeps it.
      let persisted = false
      await sendMessage({
        organizationId,
        message: trimmed,
        contextRefs,
        pageContext: { route: pathname, pageTitle },
        autoApprove: false,
        skillId: BILLING_COMPOSER_SKILL_ID,
        attachmentIds: sentAttachments.map((attachment) => attachment.attachmentId),
        onEvent: (event) => {
          if (!persisted) {
            persisted = true
            persistConversationId()
          }
          setState((prev) => reduceEvent(prev, event, assistantLineId))
        }
      })
      persistConversationId()
    },
    [
      entity,
      entityId,
      entityName,
      getFormSnapshot,
      mode,
      organizationId,
      pageTitle,
      pathname,
      persistConversationId,
      sendMessage,
      takeReady,
      waitForUploads
    ]
  )

  const answerClarification = useCallback(
    (lineId: string, answerText: string, displayText: string, answers: Record<string, string>) => {
      setState((prev) => ({
        ...prev,
        messages: prev.messages.map((line) => (line.id === lineId ? { ...line, clarificationAnswers: answers } : line))
      }))
      return send(answerText, { displayText })
    },
    [send]
  )

  const abort = useCallback(() => {
    abortStream()
    setState((prev) => ({ ...prev, busy: false, status: null, messages: finalizeStreaming(prev.messages) }))
  }, [abortStream])

  // Pending files belong to the conversation they were uploaded to, so a new chat drops them.
  const newChat = useCallback(() => {
    resetConversation()
    clearAttachments()
    writeSessionConversationId(pathname, null, sessionScope)
    setState(INITIAL_STATE)
  }, [clearAttachments, pathname, resetConversation, sessionScope])

  /**
   * Restores the conversation this page started earlier in the session (if any): replays its messages and
   * the drafts recorded in their tool traces, then points the next turn at the same conversation id.
   */
  const resumeFromSession = useCallback(async () => {
    if (resumeAttemptedRef.current || !organizationId) return
    resumeAttemptedRef.current = true
    const conversationId = readSessionConversationId(pathname, sessionScope)
    if (!conversationId) return

    setState((prev) => ({ ...prev, resuming: true }))
    try {
      const detail = await fetchConversation({ conversationId, 'X-Organization-Id': organizationId }).unwrap()
      if (detail.readOnly) {
        writeSessionConversationId(pathname, null, sessionScope)
        return
      }
      const persisted = detail.messages ?? []
      const drafts: BillingDraft[] = []
      const messages: ComposerMessage[] = mapPersistedMessages(persisted).map((message) => {
        const source = persisted.find((candidate) => candidate.id === message.id)
        const turnDrafts = billingDraftsFromTrace(parseToolTrace(source?.toolTrace), message.id)
        drafts.push(...turnDrafts)
        return {
          id: message.id,
          role: message.role,
          text: message.content,
          thinking: message.thinking || undefined,
          toolCalls: message.toolCalls,
          attachments: message.attachments,
          draftIds: turnDrafts.length > 0 ? turnDrafts.map((draft) => draft.draftId) : undefined
        }
      })
      resumeConversation(conversationId)
      setState((prev) => ({
        ...prev,
        messages,
        drafts,
        taxGuidance: latestTaxGuidance(drafts) ?? prev.taxGuidance,
        completeness: latestCompleteness(drafts) ?? prev.completeness
      }))
    } catch (error) {
      logger.warn('Billing composer: could not resume conversation', error)
      writeSessionConversationId(pathname, null, sessionScope)
    } finally {
      setState((prev) => ({ ...prev, resuming: false }))
    }
  }, [fetchConversation, organizationId, pathname, resumeConversation, sessionScope])

  // A different org means a different conversation: drop the transcript along with the stream's id.
  const lastOrgRef = useRef(organizationId)
  useEffect(() => {
    if (lastOrgRef.current !== organizationId) {
      lastOrgRef.current = organizationId
      resumeAttemptedRef.current = false
      setState(INITIAL_STATE)
    }
  }, [organizationId])

  return { ...state, send, answerClarification, abort, newChat, resumeFromSession, attachments }
}

function latestTaxGuidance(drafts: BillingDraft[]): BillingTaxGuidance | null {
  for (let index = drafts.length - 1; index >= 0; index -= 1) {
    const guidance = drafts[index]?.taxGuidance
    if (guidance) return guidance
  }
  return null
}

function latestCompleteness(drafts: BillingDraft[]): BillingCompleteness | null {
  for (let index = drafts.length - 1; index >= 0; index -= 1) {
    const completeness = drafts[index]?.completeness
    if (completeness) return completeness
  }
  return null
}

export function reduceEvent(
  prev: BillingComposerState,
  event: AssistantStreamEvent,
  assistantLineId: string
): BillingComposerState {
  switch (event.type) {
    case 'delta':
      return { ...prev, messages: updateLine(prev.messages, assistantLineId, (line) => ({ ...line, text: line.text + event.text })) }
    case 'thinking_delta':
      return {
        ...prev,
        messages: updateLine(prev.messages, assistantLineId, (line) => ({ ...line, thinking: (line.thinking ?? '') + event.text }))
      }
    case 'tool_call':
      return {
        ...prev,
        status: humanizeTool(event.name),
        messages: updateLine(prev.messages, assistantLineId, (line) => ({
          ...line,
          toolCalls: [
            ...(line.toolCalls ?? []),
            { id: `${assistantLineId}-${(line.toolCalls?.length ?? 0) + 1}`, name: event.name, status: 'running' }
          ]
        }))
      }
    case 'tool_result':
      return {
        ...prev,
        status: event.summary ?? prev.status,
        messages: updateLine(prev.messages, assistantLineId, (line) => {
          const calls = [...(line.toolCalls ?? [])]
          const runningIndex = calls.findIndex((call) => call.name === event.name && call.status === 'running')
          if (runningIndex >= 0) {
            calls[runningIndex] = { ...calls[runningIndex]!, status: 'done', summary: event.summary }
          }
          return { ...line, toolCalls: calls }
        })
      }
    case 'billing_draft': {
      const drafts = [...prev.drafts.filter((draft) => draft.draftId !== event.draft.draftId), event.draft]
      return {
        ...prev,
        drafts,
        taxGuidance: event.draft.taxGuidance ?? prev.taxGuidance,
        completeness: event.draft.completeness ?? prev.completeness,
        status: null,
        messages: updateLine(prev.messages, assistantLineId, (line) => ({
          ...line,
          draftIds: [...(line.draftIds ?? []).filter((id) => id !== event.draft.draftId), event.draft.draftId]
        }))
      }
    }
    case 'clarification':
      return {
        ...prev,
        awaitingAnswers: true,
        busy: false,
        status: null,
        messages: updateLine(prev.messages, assistantLineId, (line) => ({
          ...line,
          streaming: false,
          clarification: { intro: event.intro, questions: event.questions }
        }))
      }
    case 'done':
    case 'cancelled':
      return { ...prev, busy: false, status: null, messages: finalizeStreaming(prev.messages) }
    case 'error':
      return { ...prev, busy: false, status: null, error: event.message, messages: finalizeStreaming(prev.messages) }
    default:
      return prev
  }
}

function updateLine(
  messages: ComposerMessage[],
  lineId: string,
  update: (line: ComposerMessage) => ComposerMessage
): ComposerMessage[] {
  return messages.map((line) => (line.id === lineId ? update(line) : line))
}

function finalizeStreaming(messages: ComposerMessage[]): ComposerMessage[] {
  return messages.map((line) =>
    line.streaming || line.toolCalls?.some((call) => call.status === 'running')
      ? {
          ...line,
          streaming: false,
          toolCalls: line.toolCalls?.map((call) => (call.status === 'running' ? { ...call, status: 'done' as const } : call))
        }
      : line
  )
}

function humanizeTool(name: string): string {
  return name.replace(/_/g, ' ')
}
